<template>
    <div class="d-flex">
        <div
            class="div-product-group"
            :style="isFulfillmentPackage && 'width: 50%'"
        >
            <DFieldLoading
                v-if="isLoadingProducts"
                :is-loading="isLoadingProducts"
            />
            <div
                v-else
                class="body"
            >
                <DSelect
                    id="product_group"
                    v-model="productGroupValue"
                    class="m-0"
                    style="flex-grow: 1"
                    :options="productGroups"
                    :disabled="changeDisabled"
                    :placeholder="__('products.products.form.select_group')"
                    @change="handleGroupChange()"
                />
                <div class="switch">
                    <DSwitch
                        v-model="displayRetired"
                        color="success"
                        variant="3d"
                        :disabled="changeDisabled || isLoadingProducts"
                        @update:checked="handleRetiredChange"
                    />
                    <label>
                        {{ __('products.products.form.also_display_retired_products') }}
                    </label>
                </div>
            </div>
            <p
                v-if="errors.product_group_id"
                class="error-msg"
            >
                {{ errors.product_group_id }}
            </p>
        </div>
        <div
            class="div-product"
            :style="isFulfillmentPackage && 'width: 50%'"
        >
            <VueSelect
                v-model="productValue"
                :close-on-select="true"
                :options="products"
                :filterable="false"
                :disabled="changeDisabled || productGroupValue === '0' || isLoadingProductsForGroup"
                :searchable="!isLoadingProducts"
                :selectable="optionsSelectable"
                :clearable="false"
                @open="onOpen"
                @close="onClose"
                @search="updateSearchText"
                @option:selected="handleProductChange"
                @option:deselected="handleProductChange"
            >
                <template #list-footer>
                    <li
                        v-show="hasNextPage"
                        ref="load"
                        class="loader"
                    >
                        {{ __('messages.loading_more_options') }}
                    </li>
                </template>
                <template #no-options="{ searching }">
                    <template v-if="searching">
                        {{ __('messages.no_results') }}
                    </template>
                    <em
                        v-else
                        style="opacity: 0.5"
                    >
                        {{ __('messages.start_typing') }}
                    </em>
                </template>
            </VueSelect>
            <p
                v-if="errors.product_id"
                class="error-msg"
            >
                {{ errors.product_id }}
            </p>
        </div>
        <div
            v-if="!isFulfillmentPackage"
            class="div-product-plan"
        >
            <DSelect
                id="product_plan"
                v-model="planValue"
                class="m-0"
                :options="plans"
                :placeholder="__('customers.contracts.select_product_plan')"
                :disabled="productValue === null"
                @change="handlePlanChange()"
            />
            <p
                v-if="errors.product_plan_id"
                class="error-msg"
            >
                {{ errors.product_plan_id }}
            </p>
        </div>
    </div>
</template>

<script>

import VueSelect from 'vue-select'
import DFieldLoading from '~@/Components/Common/DFieldLoading'

export default {
    name: 'ProductPackagesSelection',
    components: { DFieldLoading, VueSelect },
    props: {
        item: {
            type: Object,
            default: () => { return {} }
        },
        disabled: {
            type: Boolean,
            default: false,
            required: false
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        isFulfillmentPackage: {
            type: Boolean,
            default: () => { return false }
        }
    },
    emits: ['product-group-update', 'product-update', 'plan-update', 'display-retired', 'reset-product-id', 'set-product-groups'],
    data () {
        return {
            displayRetired: this.item.display_retired,
            productGroups: this.item.available_groups,
            products: this.item.available_products,
            plans: [{ label: '', value: null }, ...this.item.available_plans],
            productGroupValue: this.item.product_group_id,
            productValue: this.item.product,
            planValue: this.item.product_plan_id ? Number(this.item.product_plan_id) : null,
            observer: null,
            searchText: null,
            currentPage: 1,
            lastPage: 1,
            timeout: null,
            isLoadingProducts: false,
            isLoadingProductsForGroup: false,
            optionsSelectable: () => {
                return false
            }
        }
    },
    computed: {
        hasNextPage () {
            return this.currentPage < this.lastPage
        },
        changeDisabled () {
            return this.disabled
        }
    },
    mounted () {
        this.observer = new IntersectionObserver(this.appendProducts)
    },
    created () {
        if (this.productGroups.length === 0) {
            this.fetchProductGroups()
            this.fetchProductsInitially()
        }
    },
    methods: {
        async onOpen () {
            if (this.hasNextPage) {
                await this.$nextTick()
                this.observer.observe(this.$refs.load)
            }
            this.optionsSelectable = () => true
        },
        onClose () {
            this.observer.disconnect()
            this.optionsSelectable = () => false
        },
        async fetchProductsInitially () {
            if (this.productGroupValue === '0') {
                return
            }

            this.isLoadingProducts = true
            this.currentPage = 1

            await this.fetchProducts()
                .then((res) => {
                    this.lastPage = res.data.lastPage
                    this.products = res.data.items
                    this.isLoadingProducts = false
                    this.optionsSelectable = () => true
                })
        },
        async appendProducts ([{ isIntersecting, target }]) {
            if (isIntersecting) {
                if (this.productGroupValue === '0') {
                    return
                }

                const ul = target.offsetParent
                const scrollTop = target.offsetParent.scrollTop

                this.currentPage++

                this.isLoadingProducts = true

                await this.fetchProducts()
                    .then((res) => {
                        this.products = this.products.concat(res.data.items)
                        this.isLoadingProducts = false
                    })

                await this.$nextTick()
                ul.scrollTop = scrollTop
            }
        },
        fetchProducts () {
            const params = {
                productGroup: this.productGroupValue,
                retired: this.displayRetired ? 1 : 0,
                page: this.currentPage,
                exclude_modules: ['ff_package']
            }

            if (this.searchText) {
                params.query = this.searchText
            }

            if (this.isFulfillmentPackage === true) {
                return window.axios.get(this.$route('products.dropdown.fulfillment_package_selection_list', params))
            } else {
                return window.axios.get(this.$route('products.dropdown.selection_list', params))
            }
        },
        updateSearchText (query) {
            this.searchText = query

            if (this.timer) {
                clearTimeout(this.timer)

                this.timer = null
            }

            this.timer = setTimeout(() => {
                this.fetchProductsInitially()
            }, 500)
        },
        handleGroupChange () {
            this.productValue = null

            this.isLoadingProductsForGroup = true
            this.fetchProductsInitially().then(() => {
                this.isLoadingProductsForGroup = false
                this.$emit('product-group-update', {
                    groupId: this.productGroupValue,
                    entity: this.item,
                    availableProducts: this.products
                })
            })
        },
        handleProductChange () {
            this.planValue = null

            const plans = [{ label: '', value: null }]
            for (const plan of this.productValue.plans) {
                // Exclude one time plans
                if (plan.label !== 'One-time') {
                    plans.push(plan)
                }
            }
            this.plans = plans

            this.$emit('product-update', { product: this.productValue, entity: this.item, availablePlans: plans })
        },
        handlePlanChange () {
            this.$emit('plan-update', { planId: this.planValue, entity: this.item })
        },
        handleRetiredChange () {
            this.$emit('display-retired', { retired: this.displayRetired, entity: this.item })
            this.fetchProductGroups()
            this.fetchProductsInitially()
        },
        fetchProductGroups () {
            window.axios.get(this.$route('products.dropdown.group_selection_list', {
                retired: this.displayRetired ? 1 : 0,
                withoutPlanPrice: this.isFulfillmentPackage === true ? 1 : 0
            }))
                .then((res) => {
                    this.productGroups = res.data
                    if (this.productGroups.find(product => product.value === this.productGroupValue) === undefined) {
                        this.products = []
                        this.productValue = null
                        this.productGroupValue = '0'
                        this.$emit('reset-product-id', this.item)
                    }
                    this.$emit('set-product-groups', {
                        entity: this.item,
                        availableGroups: this.productGroups
                    })
                })
        }
    }
}
</script>

<style>
:root {
    --vs-selected-color: #5c6873 !important;
    --vs-state-disabled-bg: #e4e7ea !important;
    --vs-border-color: rgb(228, 231, 234) !important;
}
</style>
<style lang="scss" scoped>
.div-product-group {
    background-color: var(--colorAppSurface);
    width: 42%;
    padding-right: 15px;

    & .body {
        display: flex;
        align-items: center;
    }

    & .switch {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        margin-left: 10px;

        & label {
            margin-bottom: 0;
            margin-left: 5px;
        }
    }
}
.div-product {
    background-color: var(--colorAppSurface);
    width: 35%;
    padding-right: 15px;
}
.div-product-plan {
    background-color: var(--colorAppSurface);
    width: 23%;
    padding-right: 15px;
}
.error-msg {
    padding-top: 3px;
    margin-bottom: 0;
    font-size: 12px;
    color: var(--colorDanger);
}
</style>
